/**
 * Shared progress and section helpers: deriveContractRevenueAndGPForDisplay,
 * checkSectionCompletion, calculateProgress, getMissingFieldsReport, and their param types.
 */

import type { MissingFieldsReport } from '../types';
import { isValueFilled } from './fieldHelpers';
import { getCombinationData } from './combinationHelpers';

/** Parameters for recalculateFields (used by facade and template logic). */
export interface RecalculateFieldsParams {
  updatedData: Record<string, any>;
  changedField?: string;
  productType: 'direct' | 'indirect' | null;
  templateData?: any;
  analysisType?: string;
  contractPriceRanking?: Array<{ price_rank: number; price: number; price_eu?: number }>;
  templateType?: string;
  supplierName?: string;
  yearIndex?: number;
}

/**
 * Derive contractAnnualRevenue and contractAnnualGP from primary fields.
 * Use at display/render time so Gross Profit and GP/Unit are correct regardless of stored state.
 */
export const deriveContractRevenueAndGPForDisplay = (data: any): any => {
  if (!data || typeof data !== 'object') return data || {};
  const usage = parseFloat(data.ncEstAnnualUsage || data.targetAnnualUsage || '0') || 0;
  const bidPrice =
    parseFloat(data.suggestedBidPrice || data.suggested_bid_price || '0') || 0;

  // Distinguish between gsmsProfit being explicitly set (including 0) vs. missing/empty.
  // When missing, fall back to stored contractAnnualGP so copy-for-all-years values are preserved.
  const gsmsProfitRaw = data.gsmsProfit ?? data.gsms_profit;
  const hasExplicitGsmsProfit =
    gsmsProfitRaw !== undefined && gsmsProfitRaw !== null && gsmsProfitRaw !== '';
  const profit = hasExplicitGsmsProfit ? (parseFloat(String(gsmsProfitRaw)) || 0) : null;

  if (usage > 0 && bidPrice > 0) {
    const revenue = usage * bidPrice;
    if (profit !== null) {
      // gsmsProfit is explicitly present — always derive GP from it (even when 0).
      const gp = profit !== 0 ? revenue * (profit / 100) : 0;
      return { ...data, contractAnnualRevenue: revenue.toFixed(2), contractAnnualGP: gp.toFixed(2) };
    }
    // gsmsProfit not present — use stored contractAnnualGP if available (e.g. set by copyForAllYears),
    // otherwise fall back to 0.
    const storedGP = parseFloat(data.contractAnnualGP || '0') || 0;
    return { ...data, contractAnnualRevenue: revenue.toFixed(2), contractAnnualGP: storedGP.toFixed(2) };
  }
  return { ...data };
};

export interface CheckSectionCompletionParams {
  sectionId: string;
  currentData: any;
  formData: any;
  supplierName: string;
  marketSalesTrendData: any[];
  contractPriceRanking: any[];
  ndcOptions?: Array<any>;
}

export const checkSectionCompletion = (params: CheckSectionCompletionParams): 'completed' | 'warning' => {
  const { sectionId, currentData, formData, supplierName, marketSalesTrendData, contractPriceRanking, ndcOptions = [] } = params;

  switch (sectionId) {
    case 'project-details':
    case 'project-information': {
      const supplierNameValue = formData.supplierName || supplierName;
      const hasAnalysisDate = isValueFilled(formData.analysisDate);
      const hasSupplierName = isValueFilled(supplierNameValue);
      const hasContracts = isValueFilled(formData.contracts);
      const isCompleted = hasAnalysisDate && hasSupplierName && hasContracts;
      return isCompleted ? 'completed' : 'warning';
    }
    case 'product-info': {
      const hasConceptName = isValueFilled(currentData.conceptName);
      const hasNdcOptions = ndcOptions && ndcOptions.length > 0;
      const hasSelectedMfgNdc = hasNdcOptions ? isValueFilled(currentData.selectedMfgNdc) : true;
      const isProductInfoCompleted = hasConceptName && hasSelectedMfgNdc;
      return isProductInfoCompleted ? 'completed' : 'warning';
    }
    case 'current-market-state': {
      const isNCTemplate = currentData.vaAnnualNcEst !== undefined ||
                          currentData.dodAnnualNcEst !== undefined ||
                          currentData.ihsAnnualNcEst !== undefined ||
                          currentData.bopAnnualNcEst !== undefined;

      if (isNCTemplate) {
        const hasStartDate = isValueFilled(currentData.usagePeriodStartDate);
        const hasEndDate = isValueFilled(currentData.usagePeriodEndDate);
        const ncEstAnnualUsageValue = currentData.ncEstAnnualUsage;
        const parsedNcEstAnnualUsage = typeof ncEstAnnualUsageValue === 'string'
          ? parseFloat(ncEstAnnualUsageValue.trim())
          : parseFloat(String(ncEstAnnualUsageValue));
        const isNcEstAnnualUsageValid = ncEstAnnualUsageValue !== null &&
            ncEstAnnualUsageValue !== undefined &&
            ncEstAnnualUsageValue !== '' &&
            ncEstAnnualUsageValue !== 0 &&
            ncEstAnnualUsageValue !== '0' &&
            ncEstAnnualUsageValue !== '0.00' &&
            !isNaN(parsedNcEstAnnualUsage) &&
            parsedNcEstAnnualUsage > 0;
        const isCompleted = hasStartDate && hasEndDate && isNcEstAnnualUsageValid;
        return isCompleted ? 'completed' : 'warning';
      } else {
        const vaAnnualUsageValue = currentData.vaAnnualUsage;
        const dodAnnualUsageValue = currentData.dodAnnualUsage;
        const isVaAnnualUsageFilled = vaAnnualUsageValue !== null &&
            vaAnnualUsageValue !== undefined &&
            vaAnnualUsageValue !== '' &&
            vaAnnualUsageValue !== 0 &&
            vaAnnualUsageValue !== '0' &&
            vaAnnualUsageValue !== '0.00' &&
            (typeof vaAnnualUsageValue === 'string' ? vaAnnualUsageValue.trim() !== '' && parseFloat(vaAnnualUsageValue) !== 0 : parseFloat(String(vaAnnualUsageValue)) !== 0);
        const isDodAnnualUsageFilled = dodAnnualUsageValue !== null &&
            dodAnnualUsageValue !== undefined &&
            dodAnnualUsageValue !== '' &&
            dodAnnualUsageValue !== 0 &&
            dodAnnualUsageValue !== '0' &&
            dodAnnualUsageValue !== '0.00' &&
            (typeof dodAnnualUsageValue === 'string' ? dodAnnualUsageValue.trim() !== '' && parseFloat(dodAnnualUsageValue) !== 0 : parseFloat(String(dodAnnualUsageValue)) !== 0);
        return isVaAnnualUsageFilled && isDodAnnualUsageFilled ? 'completed' : 'warning';
      }
    }
    case 'cost-analysis': {
      const mfgBottleSizeValue = currentData.mfgBottleSize;
      const gsmsPackSizeValue = currentData.gsmsPackSize;
      const isMfgBottleSizeFilled = mfgBottleSizeValue !== null &&
          mfgBottleSizeValue !== undefined &&
          mfgBottleSizeValue !== '' &&
          (typeof mfgBottleSizeValue === 'string' ? mfgBottleSizeValue.trim() !== '' : true);
      const isGsmsPackSizeFilled = gsmsPackSizeValue !== null &&
          gsmsPackSizeValue !== undefined &&
          gsmsPackSizeValue !== '' &&
          (typeof gsmsPackSizeValue === 'string' ? gsmsPackSizeValue.trim() !== '' : true);
      const gsmsCostFromMfgValue = currentData.gsmsCostFromMfg;
      const gsmsCostValue = currentData.gsmsCost;
      const suggestedBidPriceForCogs = currentData.suggestedBidPrice;
      const isSuggestedBidPriceNotInputtedForCogs =
          suggestedBidPriceForCogs === null ||
          suggestedBidPriceForCogs === undefined ||
          (typeof suggestedBidPriceForCogs === 'string' && suggestedBidPriceForCogs.trim() === '') ||
          suggestedBidPriceForCogs === '';
      const isGsmsCostFromMfgFilled = gsmsCostFromMfgValue !== null &&
          gsmsCostFromMfgValue !== undefined &&
          gsmsCostFromMfgValue !== '';
      const isGsmsCostFilled = gsmsCostValue !== null &&
          gsmsCostValue !== undefined &&
          gsmsCostValue !== '';
      if (!isMfgBottleSizeFilled || !isGsmsPackSizeFilled || isSuggestedBidPriceNotInputtedForCogs) {
        return 'warning';
      }
      return (isGsmsCostFromMfgFilled && isGsmsCostFilled) ? 'completed' : 'warning';
    }
    case 'pricing-strategy': {
      const suggestedBidPriceValue = currentData.suggestedBidPrice;
      const wacValue = currentData.wac;
      const isSuggestedBidPriceNotInputted =
          suggestedBidPriceValue === null ||
          suggestedBidPriceValue === undefined ||
          (typeof suggestedBidPriceValue === 'string' && suggestedBidPriceValue.trim() === '') ||
          suggestedBidPriceValue === '';
      const isSuggestedBidPriceFilled = !isSuggestedBidPriceNotInputted;
      const isWacNotInputted =
          wacValue === null ||
          wacValue === undefined ||
          (typeof wacValue === 'string' && wacValue.trim() === '') ||
          wacValue === '';
      const isWacFilled = !isWacNotInputted;
      return isSuggestedBidPriceFilled && isWacFilled ? 'completed' : 'warning';
    }
    case 'competitive-analysis':
      return marketSalesTrendData.length > 0 ? 'completed' : 'warning';
    case 'contract-price-ranking': {
      const hasContractPriceRanking = contractPriceRanking && contractPriceRanking.length > 0;
      return hasContractPriceRanking ? 'completed' : 'warning';
    }
    case 'tmop-calculator': {
      const tmopPercentValue = currentData.tmopPercent;
      const isTmopPercentFilled = tmopPercentValue !== null &&
          tmopPercentValue !== undefined &&
          tmopPercentValue !== '' &&
          tmopPercentValue !== 0 &&
          tmopPercentValue !== '0' &&
          tmopPercentValue !== '0.00' &&
          (typeof tmopPercentValue === 'string' ? tmopPercentValue.trim() !== '' && parseFloat(tmopPercentValue) !== 0 : parseFloat(String(tmopPercentValue)) !== 0);
      return isTmopPercentFilled ? 'completed' : 'warning';
    }
    default:
      return 'warning';
  }
};

export interface CalculateProgressParams {
  templateType: string;
  bidDetails: any;
  packSizeTabs: Array<{ index: number; packSize: string; conceptName: string }>;
  packSizeFormData: Record<number, any>;
  yearFormData?: Record<number, Record<number, any>>;
  formData: any;
  supplierName: string;
  marketSalesTrendData: any[];
  contractPriceRanking: any[];
}

export const calculateProgress = (params: CalculateProgressParams): number => {
  const { templateType, bidDetails, packSizeTabs, packSizeFormData, yearFormData = {}, formData, supplierName } = params;

  if (templateType === 'nc') {
    if (packSizeTabs.length === 0) {
      return 0;
    }

    const supplierNameValue = supplierName || formData.supplierName || '';
    const hasAnalysisDate = isValueFilled(formData.analysisDate);
    const hasSupplierName = isValueFilled(supplierNameValue);
    const hasContracts = isValueFilled(formData.contracts);
    const hasGsmsHndlgType = isValueFilled(formData.gsmsHndlgType);
    const isProjectInfoCompleted = hasAnalysisDate && hasSupplierName && hasContracts && hasGsmsHndlgType;

    let totalFields = 1;
    let completedFields = isProjectInfoCompleted ? 1 : 0;

    packSizeTabs.forEach((tab) => {
      for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
        const yearData = yearFormData[tab.index]?.[yearIndex] || {};
        const packSizeBaseData = packSizeFormData[tab.index] || {};
        const combinationYearData = {
          ...packSizeBaseData,
          ...yearData,
        };

        totalFields += 5;

        const hasProductInfo = isValueFilled(combinationYearData.selectedMfgNdc) ||
                              isValueFilled(combinationYearData.gsms_mfg_ndc) ||
                              isValueFilled(combinationYearData.mfg_ndc);
        if (hasProductInfo) completedFields += 1;

        const gsmsCostFromMfgValue = combinationYearData.gsmsCostFromMfg;
        const isGsmsCostFromMfgFilled = gsmsCostFromMfgValue !== null && gsmsCostFromMfgValue !== undefined && gsmsCostFromMfgValue !== '';
        if (isGsmsCostFromMfgFilled) completedFields += 1;

        const gsmsCostValue = combinationYearData.gsmsCost;
        const isGsmsCostFilled = gsmsCostValue !== null && gsmsCostValue !== undefined && gsmsCostValue !== '';
        if (isGsmsCostFilled) completedFields += 1;

        const suggestedBidPriceValue = combinationYearData.suggestedBidPrice;
        const isSuggestedBidPriceFilled = suggestedBidPriceValue !== null &&
            suggestedBidPriceValue !== undefined &&
            suggestedBidPriceValue !== '' &&
            suggestedBidPriceValue !== 0 &&
            suggestedBidPriceValue !== '0' &&
            suggestedBidPriceValue !== '0.00' &&
            (typeof suggestedBidPriceValue === 'string' ? (suggestedBidPriceValue.trim() !== '' && parseFloat(suggestedBidPriceValue) !== 0) : parseFloat(String(suggestedBidPriceValue)) !== 0);
        if (isSuggestedBidPriceFilled) completedFields += 1;

        const wacValue = combinationYearData.wac;
        const isWacFilled = wacValue !== null && wacValue !== undefined && wacValue !== '' &&
            wacValue !== 0 && wacValue !== '0' && wacValue !== '0.00' &&
            (typeof wacValue === 'string' ? (wacValue.trim() !== '' && parseFloat(wacValue) !== 0) : parseFloat(String(wacValue)) !== 0);
        if (isWacFilled) completedFields += 1;
      }
    });

    const globalProgress = totalFields === 0 ? 0 : (completedFields / totalFields) * 100;
    return Math.round(globalProgress);
  }

  const allCombinations: Array<{ conceptName: string; packSize: string; packSizeData: any }> = [];
  if (bidDetails?.form_details?.form_data) {
    bidDetails.form_details.form_data.forEach((concept: any) => {
      const packSizes = Object.keys(concept.pack_size || {});
      packSizes.forEach((packSize) => {
        allCombinations.push({
          conceptName: concept.concept_name || '',
          packSize,
          packSizeData: concept.pack_size[packSize] || {},
        });
      });
    });
  }

  if (allCombinations.length === 0) {
    return 0;
  }

  const supplierNameValue = supplierName || formData.supplierName || '';
  const hasAnalysisDate = isValueFilled(formData.analysisDate);
  const hasSupplierName = isValueFilled(supplierNameValue);
  const hasContracts = isValueFilled(formData.contracts);
  const isProjectInfoCompleted = hasAnalysisDate && hasSupplierName && hasContracts;

  const packSizeProgresses: number[] = [];
  const totalSections = 6;

  allCombinations.forEach((combination) => {
    const combinationData = getCombinationData({
      combination: { conceptName: combination.conceptName, packSize: combination.packSize },
      packSizeTabs,
      packSizeFormData,
      formData,
    });

    let completedCount = 0;
    if (isProjectInfoCompleted) completedCount++;

    const hasProductInfo = isValueFilled(combinationData.selectedMfgNdc) || isValueFilled(combinationData.gsms_mfg_ndc) || isValueFilled(combinationData.mfg_ndc);
    if (hasProductInfo) completedCount++;

    const vaAnnualUsageValue = combinationData.vaAnnualUsage;
    const dodAnnualUsageValue = combinationData.dodAnnualUsage;
    const isVaAnnualUsageFilled = vaAnnualUsageValue !== null &&
        vaAnnualUsageValue !== undefined &&
        vaAnnualUsageValue !== '' &&
        vaAnnualUsageValue !== 0 &&
        vaAnnualUsageValue !== '0' &&
        vaAnnualUsageValue !== '0.00' &&
        (typeof vaAnnualUsageValue === 'string' ? vaAnnualUsageValue.trim() !== '' && parseFloat(vaAnnualUsageValue) !== 0 : parseFloat(String(vaAnnualUsageValue)) !== 0);
    const isDodAnnualUsageFilled = dodAnnualUsageValue !== null &&
        dodAnnualUsageValue !== undefined &&
        dodAnnualUsageValue !== '' &&
        dodAnnualUsageValue !== 0 &&
        dodAnnualUsageValue !== '0' &&
        dodAnnualUsageValue !== '0.00' &&
        (typeof dodAnnualUsageValue === 'string' ? dodAnnualUsageValue.trim() !== '' && parseFloat(dodAnnualUsageValue) !== 0 : parseFloat(String(dodAnnualUsageValue)) !== 0);
    const hasMarketState = isVaAnnualUsageFilled && isDodAnnualUsageFilled;
    if (hasMarketState) completedCount++;

    const gsmsCostFromMfgValue = combinationData.gsmsCostFromMfg;
    const gsmsCostValue = combinationData.gsmsCost;
    const suggestedBidPriceForCogsProgress = combinationData.suggestedBidPrice;
    const isSuggestedBidPriceNotInputtedForCogsProgress =
        suggestedBidPriceForCogsProgress === null ||
        suggestedBidPriceForCogsProgress === undefined ||
        suggestedBidPriceForCogsProgress === '' ||
        suggestedBidPriceForCogsProgress === 0 ||
        suggestedBidPriceForCogsProgress === '0' ||
        suggestedBidPriceForCogsProgress === '0.00' ||
        (typeof suggestedBidPriceForCogsProgress === 'string' && suggestedBidPriceForCogsProgress.trim() === '');
    const isGsmsCostFromMfgFilled = gsmsCostFromMfgValue !== null &&
        gsmsCostFromMfgValue !== undefined &&
        gsmsCostFromMfgValue !== '';
    const isGsmsCostFilled = gsmsCostValue !== null &&
        gsmsCostValue !== undefined &&
        gsmsCostValue !== '';
    const hasCostAnalysis = !isSuggestedBidPriceNotInputtedForCogsProgress && isGsmsCostFromMfgFilled && isGsmsCostFilled;
    if (hasCostAnalysis) completedCount++;

    const suggestedBidPriceValue = combinationData.suggestedBidPrice;
    const wacValue = combinationData.wac;
    const isSuggestedBidPriceFilled = suggestedBidPriceValue !== null &&
        suggestedBidPriceValue !== undefined &&
        suggestedBidPriceValue !== '' &&
        suggestedBidPriceValue !== 0 &&
        suggestedBidPriceValue !== '0' &&
        suggestedBidPriceValue !== '0.00' &&
        (typeof suggestedBidPriceValue === 'string' ? suggestedBidPriceValue.trim() !== '' && parseFloat(suggestedBidPriceValue) !== 0 : parseFloat(String(suggestedBidPriceValue)) !== 0);
    const isWacFilled = wacValue !== null &&
        wacValue !== undefined &&
        wacValue !== '' &&
        wacValue !== 0 &&
        wacValue !== '0' &&
        wacValue !== '0.00' &&
        (typeof wacValue === 'string' ? wacValue.trim() !== '' && parseFloat(wacValue) !== 0 : parseFloat(String(wacValue)) !== 0);
    const hasPricingStrategy = isSuggestedBidPriceFilled && isWacFilled;
    if (hasPricingStrategy) completedCount++;

    const tmopPercentValue = combinationData.tmopPercent;
    const isTmopPercentFilled = tmopPercentValue !== null &&
        tmopPercentValue !== undefined &&
        tmopPercentValue !== '' &&
        tmopPercentValue !== 0 &&
        tmopPercentValue !== '0' &&
        tmopPercentValue !== '0.00' &&
        (typeof tmopPercentValue === 'string' ? tmopPercentValue.trim() !== '' && parseFloat(tmopPercentValue) !== 0 : parseFloat(String(tmopPercentValue)) !== 0);
    if (isTmopPercentFilled) completedCount++;

    const packSizeProgress = (completedCount / totalSections) * 100;
    packSizeProgresses.push(packSizeProgress);
  });

  if (packSizeProgresses.length === 0) return 0;
  const totalProgress = packSizeProgresses.reduce((sum, progress) => sum + progress, 0);
  const averageProgress = totalProgress / packSizeProgresses.length;
  return Math.round(averageProgress);
};

export const getMissingFieldsReport = (params: CalculateProgressParams): MissingFieldsReport => {
  const { templateType, packSizeTabs, packSizeFormData, yearFormData = {}, formData, supplierName } = params;

  const report: MissingFieldsReport = {
    totalFields: 0,
    completedFields: 0,
    missingFields: 0,
    progressPercent: 0,
    globalIssues: [],
    combinationReports: [],
  };

  if (templateType !== 'nc' || packSizeTabs.length === 0) {
    return report;
  }

  const supplierNameValue = supplierName || formData.supplierName || '';
  const hasAnalysisDate = isValueFilled(formData.analysisDate);
  const hasSupplierName = isValueFilled(supplierNameValue);
  const hasContracts = isValueFilled(formData.contracts);
  const hasGsmsHndlgType = isValueFilled(formData.gsmsHndlgType);
  const isProjectInfoCompleted = hasAnalysisDate && hasSupplierName && hasContracts && hasGsmsHndlgType;

  report.totalFields = 1;
  if (isProjectInfoCompleted) {
    report.completedFields = 1;
  } else {
    if (!hasAnalysisDate) report.globalIssues.push('Analysis Date');
    if (!hasSupplierName) report.globalIssues.push('Supplier Name');
    if (!hasContracts) report.globalIssues.push('Contract(s)');
    if (!hasGsmsHndlgType) report.globalIssues.push('GSMS Handling Type');
  }

  packSizeTabs.forEach((tab) => {
    const combinationReport = {
      conceptName: tab.conceptName,
      packSize: tab.packSize,
      tabIndex: tab.index,
      yearReports: [] as Array<{
        yearIndex: number;
        yearLabel: string;
        totalFields: number;
        completedFields: number;
        missingFields: string[];
      }>,
    };

    for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
      const yearData = yearFormData[tab.index]?.[yearIndex] || {};
      const packSizeBaseData = packSizeFormData[tab.index] || {};
      const combinationYearData = {
        ...packSizeBaseData,
        ...yearData,
      };

      const yearReport = {
        yearIndex,
        yearLabel: yearIndex === 0 ? 'Base Year' : `Option Year ${yearIndex}`,
        totalFields: 5,
        completedFields: 0,
        missingFields: [] as string[],
      };

      report.totalFields += 5;

      const hasProductInfo = isValueFilled(combinationYearData.selectedMfgNdc) ||
                            isValueFilled(combinationYearData.gsms_mfg_ndc) ||
                            isValueFilled(combinationYearData.mfg_ndc);
      if (hasProductInfo) {
        report.completedFields += 1;
        yearReport.completedFields += 1;
      } else {
        yearReport.missingFields.push('Product Info (NDC)');
      }

      const gsmsCostFromMfgValue = combinationYearData.gsmsCostFromMfg;
      const isGsmsCostFromMfgFilled = gsmsCostFromMfgValue !== null && gsmsCostFromMfgValue !== undefined && gsmsCostFromMfgValue !== '';
      if (isGsmsCostFromMfgFilled) {
        report.completedFields += 1;
        yearReport.completedFields += 1;
      } else {
        yearReport.missingFields.push('GSMS Cost From Mfg');
      }

      const gsmsCostValue = combinationYearData.gsmsCost;
      const isGsmsCostFilled = gsmsCostValue !== null && gsmsCostValue !== undefined && gsmsCostValue !== '';
      if (isGsmsCostFilled) {
        report.completedFields += 1;
        yearReport.completedFields += 1;
      } else {
        yearReport.missingFields.push('GSMS Cost');
      }

      const suggestedBidPriceValue = combinationYearData.suggestedBidPrice;
      const isSuggestedBidPriceFilled = suggestedBidPriceValue !== null && suggestedBidPriceValue !== undefined &&
          suggestedBidPriceValue !== '' && suggestedBidPriceValue !== 0 &&
          suggestedBidPriceValue !== '0' && suggestedBidPriceValue !== '0.00' &&
          (typeof suggestedBidPriceValue === 'string' ? (suggestedBidPriceValue.trim() !== '' && parseFloat(suggestedBidPriceValue) !== 0) : parseFloat(String(suggestedBidPriceValue)) !== 0);
      if (isSuggestedBidPriceFilled) {
        report.completedFields += 1;
        yearReport.completedFields += 1;
      } else {
        yearReport.missingFields.push('Suggested Bid Price');
      }

      const wacValue = combinationYearData.wac;
      const isWacFilled = wacValue !== null && wacValue !== undefined && wacValue !== '' &&
          wacValue !== 0 && wacValue !== '0' && wacValue !== '0.00' &&
          (typeof wacValue === 'string' ? (wacValue.trim() !== '' && parseFloat(wacValue) !== 0) : parseFloat(String(wacValue)) !== 0);
      if (isWacFilled) {
        report.completedFields += 1;
        yearReport.completedFields += 1;
      } else {
        yearReport.missingFields.push('WAC');
      }

      if (yearReport.missingFields.length > 0) {
        combinationReport.yearReports.push(yearReport);
      }
    }

    if (combinationReport.yearReports.length > 0) {
      report.combinationReports.push(combinationReport);
    }
  });

  report.missingFields = report.totalFields - report.completedFields;
  report.progressPercent = report.totalFields === 0 ? 0 : Math.round((report.completedFields / report.totalFields) * 100);

  return report;
};
